import { nextTick } from 'vue'
import { RouteRecordRaw, createRouter, createWebHistory } from 'vue-router'

import i18n from '../i18n'
import { usePermissionStore } from '../services/permission/permission-store'
import { useUserStore } from '../stores/user-store'

export interface INavigationRoute {
  name: string
  displayName: string
  meta: { icon: string }
  children?: INavigationRoute[]
  requiredPermission?: string
}

export interface Nav {
  root: {
    name: string
    displayName: string
  }
  routes: INavigationRoute[]
}

const publicPages = ['login', 'signup', 'reset-password', 'reset-password-email']

export const makeRouter = (routes: RouteRecordRaw[]) => {
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition
      }
      // For some reason using documentation example doesn't scroll on page navigation.
      if (to.hash) {
        return { el: to.hash, behavior: 'smooth' }
      } else {
        window.scrollTo(0, 0)
      }
    },
    routes,
  })
  router.afterEach((to) => {
    const store = useUserStore()
    const newPage = router.currentRoute.value.name as string
    const isPublicPage = publicPages.includes(newPage as string)
    if (!isPublicPage) {
      store.lastPage = router.currentRoute.value.name as string
    }
    nextTick(() => {
      document.title = i18n.global.t(('menu.' + to.name?.toString()) as string) + ' | OneTeam'
    })
  })

  router.beforeEach(async (to, from, next) => {
    const userStore = useUserStore()
    const permissionStore = usePermissionStore()
    const isLoggedIn = !!userStore.jwtToken && !!userStore.userName
    const isPublicPage = publicPages.includes(to.name as string)

    if (!isLoggedIn && !isPublicPage) {
      next({ name: 'login' })
      return
    }

    // Allow access to public pages or if not logged in
    if (!isLoggedIn || isPublicPage) {
      next()
      return
    }

    // Check page permission if required
    if (to.meta?.requiredPermission) {
      const requiredPermission = to.meta.requiredPermission as string
      try {
        // First check if user is Admin
        const isAdmin = await permissionStore.isAdmin()
        if (isAdmin) {
          // Admin can access all pages
          next()
          return
        }

        // Otherwise check specific permission
        const hasPermission = await permissionStore.checkPermission(requiredPermission)
        if (!hasPermission) {
          // Insufficient permission, redirect to 404 or access denied page
          if (userStore.lastPage == to.name) {
            // Redirect to dashboard if lastPage is inaccessible
            next({ name: 'dashboard' })
            return
          } else {
            next({ name: '404' })
          }
          return
        }
      } catch (error) {
        console.error('Permission check failed:', error)
        // Permission check failed, deny access
        next({ name: '404' })
        return
      }
    }

    next()
  })

  return router
}
